import { useMemo } from 'react';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import {
  isInBetweenPlayoutSegmentKind,
  resolveVirtualInBetweenPlayout,
  type VirtualInBetweenPlayout,
} from '@/helpers/flow-control-in-between-playout.helper';
import { flowControlSlotIdsEqual } from '@/helpers/flow-control-playing-segment.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';

/** Control tab: virtual transition/resume segment row for the active playing segment (or its pointer). */
export function useFlowControlVirtualInBetweenPlayout(): VirtualInBetweenPlayout | null {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const flowToUse = flowPlan ?? flow;
  const liveRunning = flowToUse?.liveContext?.isRunning ?? false;
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationActiveSegmentKind = useFlowControlSimulationCueStore((s) => s.activeSegmentKind);
  const simulationActiveSegmentSlotId = useFlowControlSimulationCueStore((s) => s.activeSegmentSlotId);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);

  const flowId = flowToUse?.origin ? getObjectId(flowToUse.origin) : flowToUse?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flowToUse?.liveContext) : undefined;

  const cueState = useLiveContextCueState(entityRef);

  return useMemo(() => {
    if (liveRunning) {
      return resolveVirtualInBetweenPlayout({
        rows: simulationRows,
        cueState,
      });
    }

    const row = simulationRows[simulationPointer];
    const isInBetweenPointerActive =
      simulationSelectedItemIds.length > 0 &&
      isInBetweenPlayoutSegmentKind(simulationActiveSegmentKind) &&
      simulationActiveSegmentKind === row?.segmentKind &&
      flowControlSlotIdsEqual(simulationActiveSegmentSlotId, row?.slotId);

    return resolveVirtualInBetweenPlayout({
      rows: simulationRows,
      pointerRow: row,
      activeSegmentKind: simulationActiveSegmentKind,
      activeSlotId: simulationActiveSegmentSlotId,
      isInBetweenPointerActive,
    });
  }, [
    cueState,
    liveRunning,
    simulationActiveSegmentKind,
    simulationActiveSegmentSlotId,
    simulationPointer,
    simulationRows,
    simulationSelectedItemIds.length,
  ]);
}
